import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { filterListByKey } from 'utils/filterList';

import staticText from 'static/text_display.json';
import useFilters from 'containers/Base/useFilters.js';
import useAssetType from 'containers/Pages/Assets/AssetTypes/Hooks/useAssetsType';
import { assetGridActions, assetSelectors, assetFilterActions } from '../';

import { getCompanyAssetCategories } from 'reducers/securityCompany';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import isEmpty from 'lodash/isEmpty';
// import arePropsEqual from 'fast-deep-equal';
import { PAGE_URLS } from 'utils/constants';
import { detailOpenState } from 'reducers/ui';

const BaseAsset = ({
  toggleModal,
  onEdit,
  filtersRequired,
  FormComponent,
  editable,
  details,
  userAssignedDepartments,
  filters,
  defaultFilterValues,
  filterKey,
  columns,
  filterValue = [], // A list of status to accept like ['assetStatus.AVAILABLE'] and be filtered on
  loading,
}) => {
  //-------------------------------Selectors-------------------------------
  const assets = useSelector(
    state => assetSelectors.getAssetListTransformed(state),
    shallowEqual,
  );

  const filterValues = useSelector(
    state => assetSelectors.getAssetFilters(state),
    shallowEqual,
  );

  const totalEntries = useSelector(
    state => assetSelectors.getTotalRowsCount(state),
    shallowEqual,
  );
  const forceRefresh = useSelector(
    state => assetSelectors.getForceRefresh(state),
    shallowEqual,
  );
  const allowableCategories = useSelector(
    state => getCompanyAssetCategories(state),
    shallowEqual,
  );
  const detailOpen = useSelector(state => detailOpenState(state));
  //-------------------------------custom-Hooks-------------------------------
  const assetType = useAssetType();
  //-----------------------------actions-------------------------------------
  const dispatch = useDispatch();

  const dispatchSetFiltersAction = useCallback(
    ({ filters }) => {
      dispatch(assetFilterActions.setFilters({ filters }));
    },
    [dispatch],
  );

  const dispatchFetchAssetListAction = useCallback(
    ({ filters, from = 0 }) => {
      dispatch(assetGridActions.fetchGridList({ filters, from }));
    },
    [dispatch],
  );
  const dispatchAssetFilterKeyAction = useCallback(
    key => {
      dispatch(assetFilterActions.setFilterKey(key));
    },
    [dispatch],
  );
  //-----------------------------------------------------------------

  const { localFilterValues, updateFilter } = useFilters({
    filterValues: {
      ...defaultFilterValues,
      ...filterValues,
      status:
        filterValues.status ||
        (defaultFilterValues ? defaultFilterValues.status : []),
    },
  });

  const onUpdate = data => {
    dispatchFetchAssetListAction({
      filters: data,
    });
  };

  const fetchAsset = from => {
    dispatchFetchAssetListAction({
      filters: localFilterValues,
      from,
    });
  };

  // calculate the required assets by the filter value
  const filteredAssets = !isEmpty(filterValue)
    ? filterListByKey(assets, filterValue, filterKey)
    : assets;

  const AssetFormComponent = useCallback(
    props => <FormComponent {...props} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAssignedDepartments],
  );

  return (
    <LayoutWithMap
      onEdit={onEdit}
      loading={loading}
      //filters
      filtersRequired={filtersRequired}
      filterValues={localFilterValues}
      filters={filters({
        category: localFilterValues.category,
        assetType,
        userAssignedDepartments,
        allowableCategories,
      })}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: vals => {
            dispatchAssetFilterKeyAction(true);
            dispatchSetFiltersAction({ filters: vals });
          },
        })
      }
      onUpdate={onUpdate}
      FormComponent={AssetFormComponent}
      editable={editable}
      toggleModal={toggleModal}
      header="Asset"
      cols={columns}
      data={filteredAssets}
      DetailComponent={details}
      forceRefresh={forceRefresh}
      fetchData={fetchAsset}
      totalEntries={totalEntries}
      createTextHover={staticText.managerial_view.asset.create}
      defaultFilterValues={defaultFilterValues}
      persistentStateName={PAGE_URLS.ASSETS.name}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.ASSETS.link]}
    />
  );
};

BaseAsset.propTypes = {
  toggleModal: PropTypes.func,
  filterValue: PropTypes.array,
  columns: PropTypes.array,
  userAssignedDepartments: PropTypes.array,
  onEdit: PropTypes.bool,
  loading: PropTypes.bool,
  filtersRequired: PropTypes.bool,
  FormComponent: PropTypes.elementType,
  editable: PropTypes.bool,
  details: PropTypes.elementType,
  defaultFilterValues: PropTypes.array,
  filters: PropTypes.func,
  filterKey: PropTypes.string,
};

export default BaseAsset;
